import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import type {
  CreditBalance,
  ResearchAnalysisTrigger,
  ResearchRoomView,
} from '@repo/contracts';

import { creditShortfall } from '../api/client';
import { analyseRoom, roomKeys } from '../api/research-rooms';
import { creditKeys, invalidateCredits } from '../credits/use-credits';
import { logger } from '../lib/logger';
import { useResearchRoomStore } from '../store/research-room-store';
import { milestonePending } from './analysis';
import { afterCharge } from './use-research-room';

const log = logger.child({ module: 'research-analysis' });

/** Shared by every write-up mutation for a room, so the card on the board
 *  shows "writing" whichever one started it. */
export const analysisMutationKey = (roomId: string | undefined) =>
  ['research-analysis', roomId ?? 'none'] as const;

/**
 * The room's write-up on the board: asked for by the researcher, or written
 * on its own once a round this tab asked reaches a milestone.
 *
 * Only the tab that asked writes a milestone up — every tab sees the round
 * end, and the server would only hand the others the same write-up back.
 * Failing on its own is quiet: nobody asked for it.
 */
export function useRoomAnalysis(
  captureId: string | null,
  view: ResearchRoomView | null,
  /** Write up milestones from the rounds this tab asks. One instance per
   *  room does; the card's own, for Refresh, does not. */
  auto = false,
) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [shortfall, setShortfall] = useState<{
    available: number;
    required: number;
  } | null>(null);

  const analyse = useMutation({
    mutationKey: analysisMutationKey(view?.room.id),
    mutationFn: (trigger: ResearchAnalysisTrigger) => {
      if (!view) throw new Error('No room open');
      setError(null);
      setShortfall(null);
      return analyseRoom(view.room.id, trigger);
    },
    onError: (err, trigger) => {
      log.warn({ err, trigger }, 'could not write the room up');
      if (trigger === 'milestone') return;
      const short = creditShortfall(err);
      if (short) setShortfall(short);
      else
        setError(err instanceof Error ? err.message : 'Something went wrong');
    },
    onSuccess: (result) => {
      if (!captureId) return;
      queryClient.setQueryData<ResearchRoomView | null>(
        roomKeys.forCapture(captureId),
        (current) =>
          current
            ? {
                ...current,
                room: { ...current.room, analysis: result.analysis },
              }
            : current,
      );
      const remaining = result.remaining;
      if (remaining !== undefined) {
        queryClient.setQueryData<CreditBalance>(
          creditKeys.balance(),
          (current) => (current ? afterCharge(current, remaining) : current),
        );
      }
      invalidateCredits(queryClient);
    },
  });

  // A round this tab asked just ended: write up a milestone it reached.
  // Keyed on the signal alone — the view is read, not watched, so a room
  // refetch never writes anything up.
  const roundEnded = useResearchRoomStore((s) => s.askedRoundEnded);
  const handled = useRef(roundEnded);
  const latest = useRef(view);
  latest.current = view;
  const { mutate } = analyse;
  useEffect(() => {
    if (!auto || roundEnded === handled.current) return;
    handled.current = roundEnded;
    const current = latest.current;
    if (!current || current.room.endedAt) return;
    if (milestonePending(current.messages, current.room.analysis)) {
      mutate('milestone');
    }
  }, [auto, mutate, roundEnded]);

  return {
    analyse: (trigger: ResearchAnalysisTrigger) => {
      analyse.mutate(trigger);
    },
    error,
    pending: analyse.isPending,
    shortfall,
  };
}
